// src/components/Login.js

import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Spinner, Text, Alert, AlertIcon } from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { routes } from '../config/auth.config';

/**
 * Login component that automatically redirects to Auth0 login
 * Shows loading state and handles errors gracefully
 */
const Login = () => {
  const { loginWithRedirect, isAuthenticated, isLoading, handleRedirectCallback } = useAuth0();
  const [loginError, setLoginError] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const initiateLogin = async () => {
      // Check if this is a callback from Auth0 (has code and state params)
      // Let the SDK handle the callback - don't trigger another login
      const hasAuthParams = searchParams.has('code') && searchParams.has('state');
      if (hasAuthParams) {
        console.log('Auth callback detected, waiting for SDK to process...');
        return;
      }

      // If already authenticated, redirect to dashboard
      if (isAuthenticated) {
        navigate(routes.dashboard);
        return;
      }

      // Don't trigger login while still loading auth state
      if (isLoading) {
        return;
      }

      // Attempt to redirect to Auth0 login
      try {
        await loginWithRedirect({
          appState: {
            returnTo: routes.dashboard,
          },
        });
      } catch (error) {
        console.error('Login error:', error);
        setLoginError(error.message || 'Failed to initiate login');
      }
    };

    initiateLogin();
  }, [loginWithRedirect, isAuthenticated, isLoading, navigate, searchParams]);

  if (loginError) {
    return (
      <Box p={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {loginError}. Please try again or contact support.
        </Alert>
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minH="400px" flexDirection="column">
      <Spinner size="xl" color="teal.500" thickness="4px" speed="0.65s" />
      <Text mt={4} color="gray.600">Redirecting to login...</Text>
    </Box>
  );
};

export default Login;
